// https://github.com/WizardComputer/stimulus-multiselect/blob/main/src/multiselect.js with a lot custom changes

import { Controller } from "@hotwired/stimulus"
import { avatarHtml, chipAvatarHtml } from '../utils/avatar'
import { createModalBehavior } from '../utils/modal_behavior'

const activeSelector = "[aria-selected='true']"

// Connects to data-controller="multiselect"
export default class extends Controller {
  static targets = [
    "hidden",
    "list",
    "search",
    "preview",
    "item",
    "modal",
    "modalBox",
  ]

  static values = {
    items: Array,
    selected: Array,
    searchUrl: String,
    searchRemote: { type: Boolean, default: false },
    preloadUrl: String,
    addableUrl: String,
    disabled: { type: Boolean, default: false },
  }

  connect() {
    this.behavior = createModalBehavior({
      getBackdrop: () => (this.hasModalTarget ? this.modalTarget : null),
      getModal: () => this.modalBoxTarget,
    })

    this.disconnectSwipeClose = this.behavior.listenForSwipeClose(() => this.close())

    // do not create another multiselect if multiselect already exists
    const localHandler = this.element.querySelector('.multiselect__handler')
    if (localHandler) return

    this.hiddenTarget.insertAdjacentHTML("afterend", this.template)
    if (this.selectedValue.length) this.selectedValueChanged()
    this.search = debounce(this.search.bind(this), 300)
    this.enhanceHiddenSelect()
    if (this.preloadUrlValue) this.preload()
  }

  disconnect() {
    this.disconnectSwipeClose()
  }

  isOpen() {
    return this.behavior.isOpen()
  }

  // Allows selecting the hidden select field from html and extracting selected id values:
  // document.getElementById("selectId").values - [2, 4, 23]
  enhanceHiddenSelect() {
    Object.defineProperty(this.hiddenTarget, "values", {
      get: () => {
        if (this.selectedValue.length <= 0) return []

        return this.selectedValue.map((item) => item.value)
      },
    })
  }

  search() {
    if (this.searchRemoteValue) return this.searchRemote()

    this.searchLocal()
  }

  async searchRemote() {
    if (this.searchTarget.value === "") return

    const response = await fetch(`${this.searchUrlValue}?${new URLSearchParams({
      search: this.searchTarget.value,
      preselects: this.selectedValue.map((x) => x.value).join(","),
    })}`)

    const searchedItems = await response.json()

    this.itemsValue = searchedItems
  }

  searchLocal() {
    if (this.searchTarget.value === "") {
      let theRestOfTheItems = this.itemsValue.filter((x) => !this.selectedValue.map((y) => y.value).includes(x.value))
      this.listTarget.innerHTML = this.selectedItems
      this.listTarget.insertAdjacentHTML("beforeend", this.items(theRestOfTheItems))
      return
    }

    let searched = this.itemsValue.filter((item) => {
      return item.text.toLowerCase().includes(this.searchTarget.value.toLowerCase())
    })

    let selectedSearched = this.selectedValue.filter((item) => {
      return item.text.toLowerCase().includes(this.searchTarget.value.toLowerCase())
    })

    searched = searched.filter((x) => !selectedSearched.map((y) => y.value).includes(x.value))

    if (searched.length === 0 && selectedSearched.length === 0) {
      return this.listTarget.innerHTML = this.noResultsTemplate
    }

    this.listTarget.innerHTML = this.items(selectedSearched, true)
    this.listTarget.insertAdjacentHTML("beforeend", this.items(searched))
  }

  async preload() {
    const response = await fetch(this.preloadUrlValue, { headers: { "Accept": "application/json" } })

    const items = await response.json()
    this.itemsValue = items
  }

  open() {
    this.behavior.open({
      onDesktopOpen: () => this.searchTarget.focus(),
    })
  }

  close() {
    this.behavior.close()
  }

  searchUrlValueChanged() {
    if (this.searchUrlValue) this.searchRemoteValue = true
  }

  itemsValueChanged() {
    if (!this.hasListTarget) return

    if (this.itemsValue.length) {
      this.listTarget.innerHTML = this.items(this.itemsValue)

      this.selectedValue.forEach((selected) => {
        this.checkItem(selected.value)
      })
    } else {
      this.listTarget.innerHTML = this.noResultsTemplate
    }
  }

  selectedValueChanged() {
    if (!this.hasPreviewTarget) return

    while (this.hiddenTarget.options.length) this.hiddenTarget.remove(0)

    if (this.selectedValue.length > 0) {
      this.previewTarget.innerHTML = `
        <div class="multiselect__preview-inner">
          <i class="bi bi-search multiselect__preview-lead"></i>
          <div class="multiselect__chips-wrap">
            ${this.pills}
          </div>
          <button type="button" class="multiselect__edit-pill">
            <i class="bi bi-pencil"></i>
          </button>
        </div>
      `

      this.selectedValue.forEach((selected) => {
        const option = document.createElement("option")
        option.text = selected.text
        option.value = selected.value
        option.setAttribute("selected", true)
        this.hiddenTarget.add(option)
      })

      if (!this.searchRemoteValue) {
        this.selectedValue.forEach((selected) => {
          this.checkItem(selected.value)
        })
      }
    } else {
      this.previewTarget.innerHTML = this.previewInputTemplate
    }

    this.element.dispatchEvent(new Event("multiselect-change"))
  }

  removeItem(e) {
    e.stopPropagation()

    const itemToRemove = e.currentTarget.parentNode

    this.selectedValue = this.selectedValue.filter((x) => x.value.toString() !== itemToRemove.dataset.value)
    this.uncheckItem(itemToRemove.dataset.value)
    this.element.dispatchEvent(new CustomEvent("multiselect-removed", { detail: { id: itemToRemove.dataset.value } }))
  }

  uncheckItem(value) {
    const itemToUncheck = this.listTarget.querySelector(`input[data-value="${value}"]`)

    if (itemToUncheck) itemToUncheck.checked = false
  }

  checkItem(value) {
    const itemToCheck = this.listTarget.querySelector(`input[data-value="${value}"]`)

    if (itemToCheck) itemToCheck.checked = true
  }

  toggleItem(input) {
    const item = {
      text: input.dataset.text,
      value: input.dataset.value,
    }
    let newSelectedArray = this.selectedValue

    if (input.checked) {
      newSelectedArray.push(item)

      if (this.focusedItem) {
        this.focusedItem.closest("li").classList.remove("multiselect__focused")
        this.focusedItem.removeAttribute("aria-selected")
      }

      input.setAttribute("aria-selected", "true")
      input.closest("li").classList.add("multiselect__focused")
      this.element.dispatchEvent(new CustomEvent("multiselect-added", { detail: { item } }))
    } else {
      newSelectedArray = newSelectedArray.filter((selected) => selected.value.toString() !== item.value)
      this.element.dispatchEvent(new CustomEvent("multiselect-removed", { detail: { id: item.value } }))
    }

    this.selectedValue = newSelectedArray
  }

  onKeyDown(e) {
    const handler = this[`on${e.key}Keydown`]
    if (handler) handler(e)
  }

  onArrowDownKeydown = (event) => {
    const item = this.sibling(true)
    if (item) this.navigate(item)
    event.preventDefault()
  }

  onArrowUpKeydown = (event) => {
    const item = this.sibling(false)
    if (item) this.navigate(item)
    event.preventDefault()
  }

  onBackspaceKeydown = () => {
    if (this.searchTarget.value !== "") return
    if (!this.selectedValue.length) return

    const selected = this.selectedValue
    const value = selected.pop().value

    this.uncheckItem(value)
    this.selectedValue = selected
    this.element.dispatchEvent(new CustomEvent("multiselect-removed", { detail: { id: value } }))
  }

  onEnterKeydown = (e) => {
    if (this.focusedItem) this.focusedItem.click()
  }

  onEscapeKeydown = () => {
    if (this.searchTarget.value !== "") {
      this.searchTarget.value = ""
      return this.search()
    }

    this.close()
  }

  sibling(next) {
    const options = this.itemTargets
    const selected = this.focusedItem
    const index = options.indexOf(selected)
    const sibling = next ? options[index + 1] : options[index - 1]
    const def = next ? options[0] : options[options.length - 1]
    return sibling || def
  }

  async addable(e) {
    e.preventDefault()
    const query = this.searchTarget.value

    if (query === "" || this.itemsValue.some((item) => item.text === query)) return

    const response = await fetch(this.addableUrlValue, {
      method: "POST",
      body: JSON.stringify({ addable: query }),
    })
    if (response.ok) {
      const addedItem = await response.json()

      this.addAddableItem(addedItem)
    }
  }

  addAddableItem(addedItem) {
    this.itemsValue = this.itemsValue.concat(addedItem)
    this.selectedValue = this.selectedValue.concat(addedItem)
    this.searchTarget.value = ""
    this.element.dispatchEvent(new CustomEvent("multiselect-added", { detail: { item: addedItem } }))
  }

  navigate(target) {
    const previouslySelected = this.focusedItem
    if (previouslySelected) {
      previouslySelected.removeAttribute("aria-selected")
      previouslySelected.closest("li").classList.remove("multiselect__focused")
    }

    target.setAttribute("aria-selected", "true")
    target.closest("li").classList.add("multiselect__focused")
    target.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  get focusedItem() {
    return this.listTarget.querySelector(activeSelector)
  }

  addableEvent() {
    document.dispatchEvent(new CustomEvent("multiselect-addable"))
  }

  get template() {
    return `
        <div class="multiselect__handler">

          <div class="multiselect__container"
              data-action="click->multiselect#open"
              tabindex="0">
            <div class="multiselect__preview" data-multiselect-target="preview">
              ${this.previewInputTemplate}
            </div>
        </div>

        <div class="multiselect-modal select-modal-backdrop hidden opacity-0" data-multiselect-target="modal">
          <div class="select-modal-box translate-y-full md:translate-y-0" data-multiselect-target="modalBox">

            <!-- HEADER -->
            <div class="px-4 py-3 border-b border-secondary flex justify-between items-center">
              <span class="font-semibold text-lg">
                ${this.element.dataset.chooseText}
              </span>

              <button type="button"
                      class="text-primary font-semibold cursor-pointer hover:text-primary-dark"
                      data-action="click->multiselect#close">
                OK
              </button>
            </div>

            <!-- SEARCH -->
            <div class="px-4 py-2 border-b border-secondary">
              ${this.inputTemplate}
            </div>

            <!-- LIST -->
            <div class="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
              <ul data-multiselect-target="list">
                ${this.allItems}
              </ul>
            </div>

            <div class="hidden md:block px-5 py-2 border-t border-secondary">
              <button type="button"
                      class="btn-submit"
                      data-action="click->multiselect#close">
                OK
              </button>
            </div>
        </div>
      </div>
    `
  }


  get noResultsTemplate() {
    if (!this.addableUrlValue) return `<div class="multiselect__no-result">${this.element.dataset.noResultsMessage}</div>`
    return `
      <div class="multiselect__no-result">
        <span class="multiselect__addable-button" data-action="click->multiselect#addableEvent">
          ${this.element.dataset.addablePlaceholder}
        </span>
      </div>
    `
  }

  get inputTemplate() {
    return `
        <input type="text" class="multiselect__search text-input-field px-3" placeholder="${this.element.dataset.placeholder}"
               tabindex="-1"
               data-multiselect-target="search" ${this.disabledValue === true ? 'disabled' : ''}
               data-action="multiselect#search keydown->multiselect#onKeyDown">
      `
  }

  get previewInputTemplate() {
    return `
      <div class="multiselect__preview-inner">
        <i class="bi bi-search multiselect__preview-lead"></i>
        <button type="button" class="multiselect__preview_search">
          ${this.element.dataset.placeholder}
        </button>
      </div>
    `
  }

  items(items, selected = false) {
    const checked = selected ? "checked" : ""
    let itemsTemplate = ""

    items.forEach((item) => itemsTemplate += this.itemTemplate(item, checked))

    return itemsTemplate
  }

  get pills() {
    let itemsTemplate = ""

    this.selectedValue.forEach((item) => itemsTemplate += this.pillTemplate(item))

    return itemsTemplate
  }

  get selectedItems() {
    return this.items(this.selectedValue, true)
  }

  get allItems() {
    return this.items(this.itemsValue)
  }

  itemTemplate(item, selected = "") {
    // Extract optional content inside parentheses example: (email@email.com)
    let mainText = item.text
    let subText = null

    if (item.text.includes("(")) {
      const [namePart, bracketPart] = item.text.split("(")
      mainText = namePart.trim()
      subText = bracketPart.replace(")", "").trim()
    }

    const subTextHtml = subText ?
      `<div class="text-xs text-font-secondary leading-tight">${subText}</div>` :
      ""

    const itemIcon = avatarHtml(item.value, mainText)

    return `
      <li>
        <label class="hover:bg-secondary flex flex-nowrap gap-2 items-center rounded-lg px-2 py-1">
          <input type="checkbox" ${selected} data-value="${item.value}" data-text="${item.text}"
          data-action="multiselect#checkBoxChange" data-multiselect-target="item" tabindex="-1">
          <span>
            ${itemIcon}
          </span>

          <div class="multiselect__pill-text">
            ${mainText}
            ${subTextHtml}
          </div>
        </label>
      </li>
    `
  }

  checkBoxChange(event) {
    event.preventDefault()
    this.searchTarget.focus()
    this.toggleItem(event.currentTarget)
  }

  pillTemplate(item) {
    let mainText = item.text

    if (item.text.includes("(")) {
      mainText = item.text.split("(")[0].trim()
    }

    if (this.disabledValue) {
      return `
        <div class="multiselect__pill" data-value="${item.value}" title="${item.text}">
          <div class="multiselect__pill-text">${mainText}</div>
        </div>
      `
    }

    const chipAvatar = chipAvatarHtml(mainText)

    return `
      <div class="multiselect__pill" data-value="${item.value}" title="${item.text}">
        ${chipAvatar}
        <div class="multiselect__pill-text">${mainText}</div>
        <button type="button" class="multiselect__pill-delete" data-action="click->multiselect#removeItem">
          <i class="bi bi-x"></i>
        </button>
      </div>
    `
  }
}

function debounce(fn, delay) {
  let timeoutId = null

  return (...args) => {
    const callback = () => fn.apply(this, args)
    clearTimeout(timeoutId)
    timeoutId = setTimeout(callback, delay)
  }
}
